
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface PythPrice {
    id: string;
    price: number;
    conf: number;
    expo: number;
    publish_time: number;
}

// Pyth Price Feed IDs (Mainnet Beta)
const PRICE_IDS: Record<string, string> = {
    'SOL': 'ef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d',
    'BTC': 'e62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43',
    'ETH': 'ff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace',
    'SUI': '23d7315113f5b1d3ba7a83604c4f0b5d6950c41295b963d5867855018cd647e3',
    'USDC': 'eaa020c61cc479712813461ce153894a96a6c00b21ed0cfc2798d1f9a9e9c94a'
};

const HERMES_URL = "https://hermes.pyth.network/v2/updates/price/latest";

/**
 * Fetches real-time price from Pyth Hermes API
 */
export const getPythPrice = async (symbol: string): Promise<PythPrice | null> => {
    const id = PRICE_IDS[symbol.toUpperCase()];
    
    // Fallback for unknown symbols to mock data logic if not in Pyth list
    if (!id) {
        return getMockPythPrice(symbol);
    }

    try {
        const response = await fetch(`${HERMES_URL}?ids[]=${id}`);
        if (!response.ok) throw new Error("Pyth fetch failed");
        
        const data = await response.json();
        const parsed = data.parsed?.[0];

        if (parsed) {
            const price = Number(parsed.price.price) * Math.pow(10, parsed.price.expo);
            return {
                id,
                price,
                conf: Number(parsed.price.conf),
                expo: parsed.price.expo,
                publish_time: parsed.price.publish_time
            };
        }
        return getMockPythPrice(symbol);
    } catch (e) {
        console.warn("Pyth API unreachable, using fallback:", e);
        return getMockPythPrice(symbol);
    }
};

// Fallback for demo stability
const getMockPythPrice = async (symbol: string): Promise<PythPrice> => {
    await new Promise(r => setTimeout(r, 200));
    const basePrice = symbol === 'SOL' ? 145 : symbol === 'BTC' ? 65000 : symbol === 'SUI' ? 1.6 : 1.0;
    const volatility = basePrice * 0.005; 
    const price = basePrice + (Math.random() * volatility - (volatility/2));

    return {
        id: 'mock-id',
        price,
        conf: price * 0.001,
        expo: -8,
        publish_time: Math.floor(Date.now() / 1000)
    };
};
